import { useQuery } from '@tanstack/react-query';
import { Terrace } from '@/types/terrace';
import { supabase } from '@/integrations/supabase/client';
import { isTerraceCurrentlyOpen } from '@/lib/utils/openingHoursUtils';
import { TerraceDataTransformer } from '@/lib/services/TerraceDataTransformer';

type Filters = {
  openNow: boolean;
  hasFood: boolean;
  petFriendly: boolean;
  servesAlcohol: boolean;
  sunnyOnly?: boolean;
}

const DEFAULT_FILTERS: Filters = {
  openNow: false,
  hasFood: false,
  petFriendly: false,
  servesAlcohol: false,
  sunnyOnly: false,
};

async function fetchTotalCount(): Promise<number> {
  const { count, error } = await supabase
    .from('terraces')
    .select('*', { count: 'exact', head: true });

  if (error) throw error;
  return count ?? 0;
}

async function fetchTerraces(filters: Filters): Promise<Terrace[]> {
  let query = supabase.from('terraces').select('*');

  if (filters.hasFood) query = query.eq('has_food', true);
  if (filters.petFriendly) query = query.eq('pet_friendly', true);
  if (filters.servesAlcohol) query = query.eq('serves_alcohol', true);
  if (filters.sunnyOnly) query = query.eq('sun_status', 'sunny');

  const { data, error } = await query;
  if (error) throw error;

  const transformed = TerraceDataTransformer.transformTerracesArray(data ?? []);

  if (!filters.openNow) return transformed;

  return transformed.filter(terrace => {
    if (!terrace.detailed_opening_hours && !terrace.openingHours) return false;
    try {
      return isTerraceCurrentlyOpen(terrace.openingHours, terrace.detailed_opening_hours);
    } catch {
      return false;
    }
  });
}

export const useTerraces = (filters: Filters = DEFAULT_FILTERS) => {
  const totalCountQuery = useQuery({
    queryKey: ['terraces-count'],
    queryFn: fetchTotalCount,
  });

  const terracesQuery = useQuery({
    queryKey: ['terraces', filters],
    queryFn: () => fetchTerraces(filters),
  });

  return {
    terraces: terracesQuery.data ?? [],
    loading: terracesQuery.isLoading,
    error: terracesQuery.error ? 'Failed to load terraces. Please try again later.' : null,
    totalCount: totalCountQuery.data ?? 0,
  };
};

export default useTerraces;
